import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaSpinner,
  FaArrowLeft,
  FaWeight,
  FaSmile,
  FaCamera,
  FaClipboardCheck,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaBed,
  FaTint,
  FaDumbbell,
  FaBullseye,
  FaExclamationCircle,
  FaStickyNote,
  FaTimes,
  FaImage,
  FaPills,
  FaHeartbeat,
  FaExclamationTriangle,
  FaBolt,
  FaUtensils,
  FaNotesMedical,
  FaUserMd,
} from 'react-icons/fa';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { doctorAPI } from '../../services/doctorAPI';
import { toast } from 'react-toastify';

const moodEmojis = ['', '😞', '😕', '😐', '🙂', '😄'];
const moodLabels = ['', 'Very Bad', 'Bad', 'Okay', 'Good', 'Great'];
const levelLabels = ['', 'Very Low', 'Low', 'Normal', 'Good', 'Excellent'];
const severityLabels = ['', 'Mild', 'Light', 'Moderate', 'Strong', 'Severe'];
const severityColors = ['', 'text-green-600', 'text-lime-600', 'text-yellow-600', 'text-orange-600', 'text-red-600'];
const severityBg = ['', 'bg-green-100', 'bg-lime-100', 'bg-yellow-100', 'bg-orange-100', 'bg-red-100'];

const sideEffectLabels = {
  nausea: 'Nausea',
  vomiting: 'Vomiting',
  diarrhea: 'Diarrhea',
  constipation: 'Constipation',
  headache: 'Headache',
  fatigue: 'Fatigue',
  dizziness: 'Dizziness',
  injection_site: 'Injection Site Reaction',
  appetite_loss: 'Loss of Appetite',
  abdominal_pain: 'Abdominal Pain',
};

const hasMedicalData = (checkIn) => {
  return checkIn.medicationTaken !== null ||
    checkIn.sideEffects ||
    checkIn.bloodPressureSystolic ||
    checkIn.bloodSugarFasting ||
    checkIn.medicalConcerns ||
    checkIn.appetiteLevel ||
    checkIn.energyLevel;
};

const DoctorClientProgress = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [activeTab, setActiveTab] = useState('medical'); // medical, timeline, photos
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});

  useEffect(() => {
    loadClientProgress();
  }, [clientId]);

  useEffect(() => {
    return () => {
      Object.values(photoUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const loadClientProgress = async () => {
    try {
      const data = await doctorAPI.getClientProgress(clientId);
      setClientData(data);
      if (data.checkInHistory?.length > 0) {
        setExpandedWeeks({ [data.checkInHistory[0].checkIn.weekNumber]: true });
      }
    } catch (error) {
      console.error('Failed to load client progress:', error);
      toast.error('Failed to load patient data');
      navigate('/doctor-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const toggleWeek = (weekNumber) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [weekNumber]: !prev[weekNumber],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatWeight = (weight) => {
    if (!weight) return '-';
    return `${weight.toFixed(1)} kg`;
  };

  const formatWeightChange = (change) => {
    if (!change) return '-';
    const prefix = change > 0 ? '+' : '';
    return `${prefix}${change.toFixed(1)} kg`;
  };

  // Get medical stats from check-ins
  const getMedicalStats = () => {
    if (!clientData?.checkInHistory) return null;

    const checkIns = clientData.checkInHistory.map(item => item.checkIn);
    const withMedication = checkIns.filter(c => c.medicationTaken === true).length;
    const withSideEffects = checkIns.filter(c => c.sideEffects).length;
    const totalCheckIns = checkIns.length;

    // Get all side effects
    const allSideEffects = {};
    checkIns.forEach(c => {
      if (c.sideEffects) {
        c.sideEffects.split(',').forEach(effect => {
          const key = effect.trim();
          allSideEffects[key] = (allSideEffects[key] || 0) + 1;
        });
      }
    });

    return {
      complianceRate: totalCheckIns > 0 ? Math.round((withMedication / totalCheckIns) * 100) : 0,
      sideEffectRate: totalCheckIns > 0 ? Math.round((withSideEffects / totalCheckIns) * 100) : 0,
      commonSideEffects: Object.entries(allSideEffects)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
    };
  };

  const medicalStats = getMedicalStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-purple-600" />
      </div>
    );
  }

  if (!clientData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/doctor-dashboard"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-600 mb-6 transition-colors"
        >
          <FaArrowLeft />
          <span>Back to Dashboard</span>
        </Link>

        {/* Client Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {clientData.firstName?.charAt(0)}
                {clientData.lastName?.charAt(0)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {clientData.firstName} {clientData.lastName}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <FaEnvelope className="text-gray-400" />
                    {clientData.email}
                  </span>
                  {clientData.phone && (
                    <span className="flex items-center gap-1">
                      <FaPhone className="text-gray-400" />
                      {clientData.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {clientData.planType && (
                <span
                  className={`px-4 py-2 rounded-lg font-medium ${
                    clientData.planType === 'GLP1'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  <FaUserMd className="inline mr-1" />
                  {clientData.planName || clientData.planType}
                </span>
              )}
              <span className="px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700">
                Week {clientData.currentWeek || 1}
              </span>
            </div>
          </div>

          {clientData.subscriptionStartDate && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <FaCalendarAlt className="text-gray-400" />
                Started: {formatDate(clientData.subscriptionStartDate)}
              </span>
              {clientData.subscriptionEndDate && (
                <span>Ends: {formatDate(clientData.subscriptionEndDate)}</span>
              )}
            </div>
          )}
        </div>

        {/* Medical Stats Overview */}
        {medicalStats && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 mb-6 border border-purple-200">
            <h2 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <FaPills className="text-purple-600" />
              Medical Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{medicalStats.complianceRate}%</p>
                <p className="text-xs text-gray-500 mt-1">Medication Compliance</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{medicalStats.sideEffectRate}%</p>
                <p className="text-xs text-gray-500 mt-1">Reported Side Effects</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{clientData.totalCheckIns}</p>
                <p className="text-xs text-gray-500 mt-1">Total Check-ins</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2">Common Side Effects</p>
                <div className="flex flex-wrap gap-1">
                  {medicalStats.commonSideEffects.length > 0 ? (
                    medicalStats.commonSideEffects.slice(0, 3).map(([effect, count]) => (
                      <span key={effect} className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">
                        {sideEffectLabels[effect] || effect}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">None reported</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Weight & Vitals Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <FaWeight className="text-2xl text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{formatWeight(clientData.currentWeight)}</p>
            <p className="text-xs text-gray-500">Current Weight</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <p className="text-2xl font-bold mb-1">
              {clientData.weightChange !== null && (
                <span className={clientData.weightChange < 0 ? 'text-green-600' : 'text-red-600'}>
                  {formatWeightChange(clientData.weightChange)}
                </span>
              )}
              {clientData.weightChange === null && <span className="text-gray-400">-</span>}
            </p>
            <p className="text-xs text-gray-500">Weight Change</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <FaClipboardCheck className="text-2xl text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{clientData.totalCheckIns}</p>
            <p className="text-xs text-gray-500">Check-ins</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <FaCamera className="text-2xl text-cyan-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{clientData.totalPhotos}</p>
            <p className="text-xs text-gray-500">Photos</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <FaSmile className="text-2xl text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {clientData.averageMood ? clientData.averageMood.toFixed(1) : '-'}
            </p>
            <p className="text-xs text-gray-500">Avg Mood</p>
          </div>
        </div>

        {/* Weight Chart */}
        {clientData.weightTrend?.length > 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Weight Progress</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={clientData.weightTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="weekNumber"
                    tickFormatter={(week) => `W${week}`}
                    stroke="#9ca3af"
                  />
                  <YAxis
                    domain={['dataMin - 2', 'dataMax + 2']}
                    tickFormatter={(val) => `${val}kg`}
                    stroke="#9ca3af"
                  />
                  <Tooltip
                    formatter={(value) => [`${value.toFixed(1)} kg`, 'Weight']}
                    labelFormatter={(week) => `Week ${week}`}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="weight"
                    stroke="#7c3aed"
                    fill="#ddd6fe"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('medical')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'medical'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FaPills />
            Medical Data
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'timeline'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Full Timeline
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'photos'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Photos
          </button>
        </div>

        {/* Medical Data Tab - Shows only medical fields */}
        {activeTab === 'medical' && (
          <div className="space-y-4">
            {clientData.checkInHistory?.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <FaPills className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Medical Check-ins Yet</h3>
                <p className="text-gray-500">
                  This patient hasn't submitted any check-ins with medical data yet.
                </p>
              </div>
            ) : (
              clientData.checkInHistory?.filter(item => hasMedicalData(item.checkIn)).map((item) => {
                const checkIn = item.checkIn;
                const isExpanded = expandedWeeks[checkIn.weekNumber];

                return (
                  <div
                    key={checkIn.id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-purple-500"
                  >
                    <button
                      onClick={() => toggleWeek(checkIn.weekNumber)}
                      className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <span className="text-purple-600 font-bold">W{checkIn.weekNumber}</span>
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Week {checkIn.weekNumber}</h3>
                          <p className="text-sm text-gray-500">{formatDate(checkIn.submittedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {checkIn.medicationTaken !== null && (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            checkIn.medicationTaken ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {checkIn.medicationTaken ? 'Compliant' : 'Missed'}
                          </span>
                        )}
                        {checkIn.sideEffects && (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            severityBg[checkIn.sideEffectsSeverity || 1]} ${severityColors[checkIn.sideEffectsSeverity || 1]
                          }`}>
                            Side Effects
                          </span>
                        )}
                        {isExpanded ? (
                          <FaChevronUp className="text-gray-400" />
                        ) : (
                          <FaChevronDown className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t px-6 py-4 bg-purple-50">
                        {/* Medication Compliance */}
                        {checkIn.medicationTaken !== null && (
                          <div className="bg-white rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                              <FaPills className="text-purple-600" />
                              Medication Compliance
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Medication Taken</p>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  checkIn.medicationTaken ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {checkIn.medicationTaken ? 'Yes' : 'No'}
                                </span>
                              </div>
                              {checkIn.missedDoses > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Missed Doses</p>
                                  <p className="font-bold text-red-600 text-lg">{checkIn.missedDoses}</p>
                                </div>
                              )}
                              {checkIn.currentDosage && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Dosage</p>
                                  <p className="font-semibold text-gray-900">{checkIn.currentDosage}</p>
                                </div>
                              )}
                              {checkIn.injectionSite && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Injection Site</p>
                                  <p className="font-semibold text-gray-900 capitalize">{checkIn.injectionSite.replace('_', ' ')}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Vital Signs */}
                        {(checkIn.bloodPressureSystolic || checkIn.bloodSugarFasting || checkIn.heartRate) && (
                          <div className="bg-white rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                              <FaHeartbeat className="text-blue-600" />
                              Vital Signs
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {checkIn.bloodPressureSystolic && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 mb-1">Blood Pressure</p>
                                  <p className="font-bold text-xl text-gray-900">
                                    {checkIn.bloodPressureSystolic}/{checkIn.bloodPressureDiastolic || '-'}
                                  </p>
                                  <p className="text-xs text-gray-500">mmHg</p>
                                </div>
                              )}
                              {checkIn.bloodSugarFasting && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 mb-1">Fasting Sugar</p>
                                  <p className="font-bold text-xl text-gray-900">{checkIn.bloodSugarFasting}</p>
                                  <p className="text-xs text-gray-500">mg/dL</p>
                                </div>
                              )}
                              {checkIn.bloodSugarPostMeal && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 mb-1">Post Meal Sugar</p>
                                  <p className="font-bold text-xl text-gray-900">{checkIn.bloodSugarPostMeal}</p>
                                  <p className="text-xs text-gray-500">mg/dL</p>
                                </div>
                              )}
                              {checkIn.heartRate && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 mb-1">Heart Rate</p>
                                  <p className="font-bold text-xl text-gray-900">{checkIn.heartRate}</p>
                                  <p className="text-xs text-gray-500">bpm</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Side Effects */}
                        {checkIn.sideEffects && (
                          <div className="bg-white rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                              <FaExclamationTriangle className="text-amber-600" />
                              Side Effects
                              {checkIn.sideEffectsSeverity && (
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-sm font-medium ${
                                  severityBg[checkIn.sideEffectsSeverity]} ${severityColors[checkIn.sideEffectsSeverity]
                                }`}>
                                  {severityLabels[checkIn.sideEffectsSeverity]}
                                </span>
                              )}
                            </h4>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {checkIn.sideEffects.split(',').map((effect, i) => (
                                <span key={i} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                                  {sideEffectLabels[effect.trim()] || effect.trim()}
                                </span>
                              ))}
                            </div>
                            {checkIn.sideEffectsNotes && (
                              <div className="bg-amber-50 rounded-lg p-3">
                                <p className="text-sm text-gray-700">{checkIn.sideEffectsNotes}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Appetite & Energy */}
                        {(checkIn.appetiteLevel || checkIn.energyLevel) && (
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            {checkIn.appetiteLevel && (
                              <div className="bg-white rounded-lg p-4 flex items-center gap-3">
                                <FaUtensils className="text-2xl text-purple-500" />
                                <div>
                                  <p className="text-xs text-gray-500">Appetite Level</p>
                                  <p className="font-bold text-lg text-gray-900">{levelLabels[checkIn.appetiteLevel]}</p>
                                </div>
                              </div>
                            )}
                            {checkIn.energyLevel && (
                              <div className="bg-white rounded-lg p-4 flex items-center gap-3">
                                <FaBolt className="text-2xl text-blue-500" />
                                <div>
                                  <p className="text-xs text-gray-500">Energy Level</p>
                                  <p className="font-bold text-lg text-gray-900">{levelLabels[checkIn.energyLevel]}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Medical Concerns */}
                        {checkIn.medicalConcerns && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                              <FaNotesMedical className="text-red-600" />
                              Patient's Medical Concerns
                            </h4>
                            <p className="text-gray-700">{checkIn.medicalConcerns}</p>
                          </div>
                        )}

                        {/* Basic Progress Info */}
                        <div className="mt-4 pt-4 border-t border-purple-200">
                          <p className="text-sm text-gray-500 mb-2">Basic Progress</p>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white rounded-lg p-3 text-center">
                              <p className="font-semibold text-gray-900">{formatWeight(checkIn.weight)}</p>
                              <p className="text-xs text-gray-500">Weight</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center">
                              <p className="text-2xl">{moodEmojis[checkIn.overallMood]}</p>
                              <p className="text-xs text-gray-500">Mood</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center">
                              <p className="font-semibold text-gray-900">{checkIn.sleepHours || '-'} hrs</p>
                              <p className="text-xs text-gray-500">Sleep</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Timeline Tab - Same as coach view */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {clientData.checkInHistory?.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <FaClipboardCheck className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Check-ins Yet</h3>
                <p className="text-gray-500">This patient hasn't submitted any check-ins yet.</p>
              </div>
            ) : (
              clientData.checkInHistory?.map((item) => {
                const checkIn = item.checkIn;
                const photos = item.photos || [];
                const isExpanded = expandedWeeks[checkIn.weekNumber];

                return (
                  <div key={checkIn.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <button
                      onClick={() => toggleWeek(checkIn.weekNumber)}
                      className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                          <span className="text-primary-600 font-bold">W{checkIn.weekNumber}</span>
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Week {checkIn.weekNumber}</h3>
                          <p className="text-sm text-gray-500">{formatDate(checkIn.submittedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-600 font-medium">{formatWeight(checkIn.weight)}</span>
                        <span className="text-2xl">{moodEmojis[checkIn.overallMood]}</span>
                        {photos.length > 0 && (
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <FaCamera />
                            {photos.length}
                          </span>
                        )}
                        {hasMedicalData(checkIn) && (
                          <FaPills className="text-purple-500" />
                        )}
                        {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Weight</p>
                            <p className="font-semibold text-gray-900">{formatWeight(checkIn.weight)}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Sleep</p>
                            <p className="font-semibold text-gray-900">{checkIn.sleepHours ? `${checkIn.sleepHours} hrs` : '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Water</p>
                            <p className="font-semibold text-gray-900">{checkIn.waterIntake ? `${checkIn.waterIntake} L` : '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Mood</p>
                            <p className="font-semibold text-gray-900">{moodEmojis[checkIn.overallMood]} {moodLabels[checkIn.overallMood]}</p>
                          </div>
                        </div>

                        {checkIn.exerciseDone && (
                          <div className="bg-white rounded-lg p-3 mb-3">
                            <p className="text-xs text-gray-500 mb-1">Exercise</p>
                            <p className="text-gray-700">{checkIn.exerciseDone}</p>
                          </div>
                        )}

                        {checkIn.additionalNotes && (
                          <div className="bg-white rounded-lg p-3 mb-3">
                            <p className="text-xs text-gray-500 mb-1">Notes</p>
                            <p className="text-gray-700">{checkIn.additionalNotes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {clientData.allPhotos?.length === 0 ? (
              <div className="text-center py-12">
                <FaCamera className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Photos Yet</h3>
                <p className="text-gray-500">This patient hasn't uploaded any progress photos yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {clientData.allPhotos?.map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo)}
                    className="relative group aspect-square rounded-lg border-2 border-gray-200 group-hover:border-purple-500 transition-colors overflow-hidden bg-gray-100"
                  >
                    {loadingImages[photo.id] ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaSpinner className="animate-spin text-gray-400" />
                      </div>
                    ) : photoUrls[photo.id] ? (
                      <img
                        src={photoUrls[photo.id]}
                        alt={photo.photoType || 'Progress'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaImage className="text-2xl text-gray-300" />
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2 right-2">
                      <span className="bg-black/60 text-white text-xs px-2 py-1 rounded">
                        Week {photo.weekNumber}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Photo Lightbox */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            >
              <FaTimes className="text-2xl" />
            </button>
            <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {photoUrls[selectedPhoto.id] ? (
                <img
                  src={photoUrls[selectedPhoto.id]}
                  alt={selectedPhoto.photoType || 'Progress'}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <FaSpinner className="animate-spin text-4xl text-white" />
                </div>
              )}
              <div className="text-center mt-4 text-white">
                <p className="font-medium">Week {selectedPhoto.weekNumber} - {selectedPhoto.photoType || 'Progress Photo'}</p>
                <p className="text-sm text-gray-300">{formatDate(selectedPhoto.uploadDate)}</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DoctorClientProgress;
